/**
 * Budget Details Modal — read-only budget view
 */

import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useRegion } from '../../contexts/RegionContext';
import type { Budget } from '../../types/budget.types';

interface BudgetDetailsModalProps {
  budget: Budget | null;
  onClose: () => void;
}

export function BudgetDetailsModal({ budget, onClose }: BudgetDetailsModalProps): React.ReactElement | null {
  const closeRef = useRef<HTMLButtonElement>(null);
  const { formatCurrency, formatDate } = useRegion();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  if (!budget) return null;

  const limits = budget.categoryLimits ?? [];

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="budget-details-title"
    >
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 id="budget-details-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Budget Details
            </h2>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{budget.name}</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 capitalize">
                  {budget.periodType}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 capitalize">
                  {budget.status}
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Current period: {budget.periodLabel ?? '—'}
                {budget.periodStart && budget.periodEnd
                  ? ` (${formatDate(budget.periodStart)} – ${formatDate(budget.periodEnd)})`
                  : ''}
              </p>
              {budget.status === 'closed' && budget.endDate && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Closed on {formatDate(budget.endDate)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Budget</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(budget.totalBudget ?? 0)}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400">Spent</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(budget.totalSpent ?? 0)}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(budget.totalRemaining ?? 0)}</p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Alert Thresholds</h4>
              <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span>Warning: {budget.alertThresholds.warning}%</span>
                <span>Critical: {budget.alertThresholds.critical}%</span>
                <span>Limit: {budget.alertThresholds.limit}%</span>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Category Limits</h4>
              {limits.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No category limits configured.</p>
              ) : (
                <div className="space-y-2">
                  {limits.map((limit) => {
                    const spent = limit.spentAmount ?? 0;
                    const remaining = (limit.amount ?? 0) - spent;
                    const pct = limit.amount ? Math.round((spent / limit.amount) * 100) : 0;
                    return (
                      <div
                        key={limit.id}
                        className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                            {limit.categoryName ?? 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatCurrency(spent)} of {formatCurrency(limit.amount)} · {pct}% used
                          </p>
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            remaining >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {formatCurrency(Math.abs(remaining))} {remaining >= 0 ? 'left' : 'over'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
